import { MouseEventHandler, useState } from 'react';
import styled from '@emotion/styled';
import { Box, BoxProps, ChipProps, Divider, Link, Menu, MenuItem } from '@mui/material';

import FilterChip from '@/components/_common/Filters/FilterChip';
import TooltipOverflow from '@/components/_common/TooltipOverflow';
import FilterSearch from '@/components/_common/Filters/FilterSearch';

type JobFilterChipProps = Omit<BoxProps, 'onChange'> & {
  label: string;
  filterOptions: string[];
  selectedOptions: string[];
  onChange: (newSelectedOptions: string[]) => void;
};

export default function JobFilterChip({ label, filterOptions, selectedOptions, onChange, ...rest }: JobFilterChipProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const hasSelections = selectedOptions.length !== 0;
  const isSearchable = filterOptions.length > 5;

  const labelContent = (
    <>
      {label}:{' '}
      <strong style={{ marginLeft: 5 }}>{selectedOptions.length > 0 ? <TooltipOverflow length={1} items={selectedOptions} /> : 'All'}</strong>
    </>
  );

  const isSelected = (option: string) => selectedOptions.includes(option);

  const handleMenuOpen: MouseEventHandler<HTMLDivElement> = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleClear: ChipProps['onDelete'] = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const handleSelectAll = () => {
    onChange([...filterOptions]);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleMenuItemClick: MouseEventHandler<HTMLLIElement> = (e) => {
    let selections = [];
    const selectedId = e.currentTarget.id;
    if (isSelected(selectedId)) {
      selections = selectedOptions.filter((x: string) => x !== selectedId);
    } else {
      selections = [...selectedOptions, selectedId];
    }
    onChange(selections);
  };

  const renderAllButtons = () => {
    // do NOT render if single select OR options is less than 5
    if (filterOptions.length < 5) return;

    let content = null;
    if (selectedOptions.length === 0) {
      content = <SelectAllButton onClick={handleSelectAll}>Select All</SelectAllButton>;
    } else if (selectedOptions.length === filterOptions.length) {
      content = <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>;
    } else if (selectedOptions.length > 0 && selectedOptions.length < filterOptions.length) {
      content = (
        <>
          <SelectAllButton onClick={handleSelectAll}>Select All</SelectAllButton>
          <Divider orientation="vertical" flexItem />
          <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>
        </>
      );
    }

    return <ButtonsContainer>{content}</ButtonsContainer>;
  };

  return (
    <Box sx={{ mr: 1 }} {...rest}>
      <FilterChip label={labelContent} isSelected={hasSelections} onClick={handleMenuOpen} onClear={handleClear} />
      <Menu
        open={Boolean(anchorEl)}
        variant="menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleMenuClose}
      >
        {isSearchable ? (
          <FilterSearch
            label={label}
            options={filterOptions}
            selectedOptions={selectedOptions}
            renderAllButtons={renderAllButtons}
            handleMenuItemClick={handleMenuItemClick}
            onClose={handleMenuClose}
          />
        ) : (
          <div key="menu-items-container">
            {renderAllButtons()}
            {filterOptions.map((option) => (
              <MenuItem selected={isSelected(option)} key={option} id={option} onKeyDown={(e) => e.stopPropagation()} onClick={handleMenuItemClick}>
                {option}
              </MenuItem>
            ))}
          </div>
        )}
      </Menu>
    </Box>
  );
}

const SelectAllButton = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};
  transition: all 0.3s ease;
  &:hover {
    color: ${(props) => props.theme.palette.primary.light};
  }
`;

const ClearAllButton = styled(Link)`
  color: ${(props) => props.theme.palette.grey[600]};
  transition: all 0.3s ease;
  &:hover {
    color: ${(props) => props.theme.palette.grey[400]};
  }
`;

const ButtonsContainer = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${(props) => props.theme.spacing(1)};
  padding: ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(2)};
  padding-top: 0;
`;
